import { jsx as _jsx } from "react/jsx-runtime";
import { ExternalContactCard } from "../external-contact-card/external-contact-card";
import { OrgFunctionCard } from "../org-function-card/org-function-card";
import { SystemCard } from "../system-card/system-card";
import { UserCard, toUserCardProps } from "../user-card/user-card";
/**
 * Pairs an entity with its model, checking that the two agree.
 *
 * The model is the authority — it is what every payload declares — and the
 * shape check only rules out the three it cannot be. Each of the other models
 * has a REQUIRED field of its own (`firstName`, `kind`, `type`), so ruling them
 * out is what leaves a system, without depending on any optional field being
 * present. `null` means the pair does not hold, which a caller renders as
 * nothing rather than as a card built from the wrong fields.
 */ export function toAssigneeCardRecord(model, entity) {
    switch(model){
        case "OrgFunction":
            return "kind" in entity ? {
                model,
                entity
            } : null;
        case "ExternalContact":
            return "type" in entity ? {
                model,
                entity
            } : null;
        case "System":
            if ("firstName" in entity) return null;
            if ("kind" in entity) return null;
            if ("type" in entity) return null;
            return {
                model,
                entity
            };
        default:
            // `User`, and the undefined default every assignee surface applies.
            return "firstName" in entity ? {
                model: "User",
                entity
            } : null;
    }
}
/**
 * The same pairing, resolved out of the org-wide {@link AssigneeLookup} — where
 * the maps are already typed per model, so nothing has to be checked.
 */ export function lookupAssigneeCardRecord(model, entityId, lookup) {
    switch(model){
        case "User":
            {
                const entity = lookup.users?.get(entityId);
                return entity ? {
                    model,
                    entity
                } : null;
            }
        case "System":
            {
                const entity = lookup.systems?.get(entityId);
                return entity ? {
                    model,
                    entity
                } : null;
            }
        case "OrgFunction":
            {
                const entity = lookup.orgFunctions?.get(entityId);
                return entity ? {
                    model,
                    entity
                } : null;
            }
        case "ExternalContact":
            {
                const entity = lookup.externalContacts?.get(entityId);
                return entity ? {
                    model,
                    entity
                } : null;
            }
        default:
            return null;
    }
}
/**
 * The card for one person, system, team or external contact — the single
 * dispatch from a model to the component that draws it.
 *
 * Exhaustive over {@link AssigneeCardRecord} and guarded by `never`, so a fifth
 * assignee model arrives as a compile error rather than as an empty box. The
 * tombstone treatment is each card's own (`deletedAt` → grayed + "(deleted)"),
 * so nothing is re-implemented here.
 */ export function AssigneeCard({ record, variants, itemVariant = "default", size = "default", className }) {
    switch(record.model){
        case "User":
            return /*#__PURE__*/ _jsx(UserCard, {
                user: toUserCardProps(record.entity),
                variant: variants.User,
                itemVariant: itemVariant,
                size: size,
                className: className
            });
        case "System":
            return /*#__PURE__*/ _jsx(SystemCard, {
                system: record.entity,
                variant: variants.System,
                itemVariant: itemVariant,
                size: size,
                className: className
            });
        case "OrgFunction":
            return /*#__PURE__*/ _jsx(OrgFunctionCard, {
                orgFunction: record.entity,
                variant: typeof variants.OrgFunction === "function" ? variants.OrgFunction(record.entity) : variants.OrgFunction,
                itemVariant: itemVariant,
                size: size,
                className: className
            });
        case "ExternalContact":
            return /*#__PURE__*/ _jsx(ExternalContactCard, {
                contact: record.entity,
                variant: variants.ExternalContact,
                itemVariant: itemVariant,
                size: size,
                className: className
            });
        default:
            {
                const unhandled = record;
                throw new Error(`unhandled entity card model: ${JSON.stringify(unhandled)}`);
            }
    }
}

//# sourceMappingURL=assignee-card.js.map